import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Send, Bookmark, ListChecks, Users } from "lucide-react";
import { useAuth } from "@/lib/auth";
import JobAlertButton from "@/components/jobs/JobAlertButton";

/**
 * Iter-263: Quick Apply CTA block for jobs-family listings. Two modes:
 *
 *   - Viewer is the listing OWNER → "View applicants" + "Manage
 *     screening questions" links (no apply button).
 *   - Viewer is a buyer/applicant → primary "Quick Apply" (routes to
 *     `/jobs/apply/{id}`), secondary "Track my applications" and a
 *     Save-heart hint (heart lives elsewhere on the page).
 *
 * The full 4-step apply flow (§3 bootstrap → documents → questions →
 * profile review → submit) lives on the `/jobs/apply/{id}` route which
 * we build out in the follow-up sprint. This CTA opens the door.
 */
export default function QuickApplyCta({ listing }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const isOwner =
    user?.id && (listing.seller?.id === user.id || listing.seller_id === user.id || listing.user_id === user.id);

  if (isOwner) {
    return (
      <div
        data-testid="job-owner-cta"
        className="mt-4 rounded-2xl bg-white border border-soft p-5"
      >
        <h2 className="font-display text-lg font-bold text-slate-800">
          Your job listing
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          Manage applicants and screening questions from your dashboard.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <Link
            to={`/jobs/employer?listing=${listing.id}`}
            data-testid="job-view-applicants-btn"
            className="inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] text-white font-bold px-5 py-2.5 transition-colors"
          >
            <Users className="w-4 h-4" /> View applicants
          </Link>
          <Link
            to={`/jobs/employer?listing=${listing.id}&tab=questions`}
            data-testid="job-manage-questions-btn"
            className="inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-blue-wash)] text-[color:var(--brand-blue)] font-bold px-5 py-2.5 border border-[color:var(--brand-blue)] transition-colors"
          >
            <ListChecks className="w-4 h-4" /> Screening questions
          </Link>
        </div>
      </div>
    );
  }

  const applied = listing.already_applied === true;
  const handleApply = () => {
    if (!user) {
      nav(`/login?next=${encodeURIComponent(`/jobs/apply/${listing.id}`)}`);
      return;
    }
    nav(`/jobs/apply/${listing.id}`);
  };

  return (
    <div
      data-testid="job-quick-apply-cta"
      className="mt-4 rounded-2xl bg-white border border-soft p-5 md:p-6"
    >
      <h2 className="font-display text-lg font-bold text-slate-800">
        Interested in this role?
      </h2>
      <p className="text-sm text-slate-500 mt-1">
        {applied
          ? "You've already applied — track your application status below."
          : "One quick form, your reusable profile and documents pre-fill for you."}
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        {applied ? (
          <Link
            to={`/jobs/applications`}
            data-testid="job-view-my-application-btn"
            className="inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] text-white font-bold px-5 py-2.5 transition-colors"
          >
            <ListChecks className="w-4 h-4" /> View my application
          </Link>
        ) : (
          <button
            type="button"
            onClick={handleApply}
            data-testid="job-quick-apply-btn"
            className="inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white font-bold px-5 py-2.5 transition-colors"
          >
            <Send className="w-4 h-4" /> Quick Apply
          </button>
        )}
        <Link
          to="/jobs/applications"
          data-testid="job-track-applications-link"
          className="inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-blue-wash)] text-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-wash)]/70 font-semibold px-4 py-2.5 transition-colors"
        >
          <Bookmark className="w-4 h-4" /> Track my applications
        </Link>
      </div>

      <SimilarJobAlertRow listing={listing} />
    </div>
  );
}

function SimilarJobAlertRow({ listing }) {
  // Iter-294 P1 follow-up — one-tap "Alert me about similar jobs" on job
  // detail pages. Derives a compact keyword from the listing title
  // (first ~4 words, punctuation stripped) and forwards the region so
  // the alert dialog opens pre-filled. Users can still tweak or clear
  // either field before saving.
  const keyword = (() => {
    const raw = (listing.title || "").trim();
    if (!raw) return "";
    const cleaned = raw.replace(/[^\p{L}\p{N}\s-]/gu, " ").replace(/\s+/g, " ").trim();
    const words = cleaned.split(" ").slice(0, 4).join(" ");
    return words.length > 40 ? words.slice(0, 40).trim() : words;
  })();
  const region = listing.region || listing.city || "";

  return (
    <div
      className="mt-4 pt-4 border-t border-subtle flex flex-wrap items-center justify-between gap-3"
      data-testid="job-similar-alert-row"
    >
      <div className="min-w-0">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-400">
          Not the right role?
        </div>
        <div className="text-sm text-slate-600 truncate">
          We&apos;ll email you when similar jobs go live.
        </div>
      </div>
      <JobAlertButton
        presetQuery={keyword}
        presetRegion={region}
        testId="job-similar-alert-btn"
      />
    </div>
  );
}
